import React from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { EditIcon, DeleteIcon } from '../components/icons';

const EMPTY_FORM = {
  name: '', trade_name: '', gst_number: '',
  address_line1: '', address_line2: '', address_line3: '',
  city: '', pincode: '', state_id: ''
};

class Hotels extends React.Component {
  state = {
    hotels: [],
    states: [],
    loading: true,
    error: '',
    saving: false,
    showModal: false,
    editing: null,
    form: EMPTY_FORM,
    uploading: false,
    uploadResult: null,
    showUploadResult: false,
    returnTo: null
  };

  fileInputRef = React.createRef();

  componentDidMount() {
    this.loadAll();

    const params = new URLSearchParams(window.location.search);
    if (params.get('openAdd') === '1') {
      const returnTo = params.get('returnTo') || null;
      this.setState({ showModal: true, editing: null, form: EMPTY_FORM, returnTo });
      // Clean the URL so a refresh doesn't re-trigger this
      window.history.replaceState({}, '', '/hotels');
    }
  }

  loadAll = async () => {
    this.setState({ loading: true, error: '' });
    try {
      const [hotelsRes, statesRes] = await Promise.all([
        api.get('/hotels'),
        api.get('/states')
      ]);
      this.setState({ hotels: hotelsRes.data, states: statesRes.data, loading: false });
    } catch (err) {
      this.setState({ error: 'Failed to load hotels.', loading: false });
    }
  };

  openAddModal = () => this.setState({ showModal: true, editing: null, form: EMPTY_FORM, error: '' });

  openEditModal = (hotel) => {
    this.setState({
      showModal: true,
      editing: hotel,
      error: '',
      form: {
        name: hotel.name || '',
        trade_name: hotel.trade_name || '',
        gst_number: hotel.gst_number || '',
        address_line1: hotel.address_line1 || '',
        address_line2: hotel.address_line2 || '',
        address_line3: hotel.address_line3 || '',
        city: hotel.city || '',
        pincode: hotel.pincode || '',
        state_id: hotel.state_id || ''
      }
    });
  };

  closeModal = () => this.setState({ showModal: false });

  handleFormChange = (e) => {
    this.setState({ form: { ...this.state.form, [e.target.name]: e.target.value }, error: '' });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { editing, form, returnTo } = this.state;
    if (!form.name.trim()) {
      this.setState({ error: 'Name Of Hotel is required.' });
      return;
    }
    this.setState({ saving: true, error: '' });
    try {
      if (editing) {
        await api.put(`/hotels/${editing.id}`, form);
        this.closeModal();
        this.loadAll();
      } else {
        const res = await api.post('/hotels', form);
        if (returnTo) {
          // Came here from the invoice wizard's "+ Add Hotel" button —
          // send the new hotel's id back so it can be auto-selected.
          window.location.href = `${returnTo}?newHotelId=${res.data.id}`;
          return;
        }
        this.closeModal();
        this.loadAll();
      }
    } catch (err) {
      this.setState({ error: err.response?.data?.message || 'Save failed. Check the values and try again.' });
    } finally {
      this.setState({ saving: false });
    }
  };

  handleDelete = async (id) => {
    if (!window.confirm('Delete this hotel?')) return;
    try {
      await api.delete(`/hotels/${id}`);
      this.loadAll();
    } catch (err) {
      this.setState({ error: 'Delete failed — it may be referenced by an invoice.' });
    }
  };

  triggerFilePicker = () => {
    this.fileInputRef.current?.click();
  };

  handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = new FormData();
    data.append('file', file);

    this.setState({ uploading: true, error: '' });
    try {
      const res = await api.post('/hotels/bulk-upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      this.setState({ uploadResult: res.data, showUploadResult: true });
      this.loadAll();
    } catch (err) {
      this.setState({ error: err.response?.data?.message || 'Bulk upload failed.' });
    } finally {
      this.setState({ uploading: false });
      e.target.value = ''; // allow re-selecting the same file later
    }
  };

  render() {
    const { hotels, states, loading, error, showModal, saving, editing, form, uploading, uploadResult, showUploadResult } = this.state;

    return (
      <Layout>
        <div className="page-header">
          <div>
            <h1 className="page-title">Hotels</h1>
            <p className="page-sub">Hotels invoices can be generated against</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              ref={this.fileInputRef}
              style={{ display: 'none' }}
              onChange={this.handleFileSelected}
            />
            <button className="btn btn-outline" onClick={this.triggerFilePicker} disabled={uploading}>
              {uploading ? 'Uploading…' : 'Upload Excel'}
            </button>
            <button className="btn btn-brass" onClick={this.openAddModal}>+ Add Hotel</button>
          </div>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {showUploadResult && uploadResult && (
          <div className="card" style={{ borderLeft: '3px solid var(--brass)' }}>
            <strong>Import finished</strong> — {uploadResult.inserted} added, {uploadResult.skipped} skipped
            {uploadResult.total ? ` (of ${uploadResult.total} rows)` : ''}.

            {uploadResult.skippedDetails?.length > 0 && (
              <p style={{ margin: '8px 0 0', fontSize: 13 }}>
                <strong>{uploadResult.skippedDetails.length}</strong> row(s) had no hotel name or trade name to import — rows: {uploadResult.skippedDetails.map((d) => d.row).join(', ')}
              </p>
            )}

            {uploadResult.errors?.length > 0 && (
              <ul style={{ marginTop: 10, paddingLeft: 20, fontSize: 13, color: 'var(--muted)' }}>
                {uploadResult.errors.slice(0, 15).map((e, i) => <li key={i}>{e}</li>)}
                {uploadResult.errors.length > 15 && <li>…and {uploadResult.errors.length - 15} more</li>}
              </ul>
            )}
            <button
              className="btn btn-outline"
              style={{ marginTop: 10, padding: '4px 12px', fontSize: 12 }}
              onClick={() => this.setState({ showUploadResult: false })}
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="card">
          {loading ? (
            <p>Loading...</p>
          ) : hotels.length === 0 ? (
            <div className="empty-state">
              <div className="glyph">—</div>
              No hotels added yet.
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Trade Name</th>
                  <th>City</th>
                  <th>State</th>
                  <th>GST No.</th>
                  <th className="actions-col">Action</th>
                </tr>
              </thead>
              <tbody>
                {hotels.map((h) => (
                  <tr key={h.id}>
                    <td data-label="Name">{h.name}</td>
                    <td data-label="Trade Name">{h.trade_name || '—'}</td>
                    <td data-label="City">{h.city || '—'}</td>
                    <td data-label="State">{h.state_name || '—'}</td>
                    <td className="mono" data-label="GST No.">{h.gst_number || '—'}</td>
                    <td className="actions-cell" data-label="Action">
                      <div className="row-actions">
                        <button className="action-btn edit" title="Edit" onClick={() => this.openEditModal(h)}>
                          <EditIcon />
                        </button>
                        <button className="action-btn delete" title="Delete" onClick={() => this.handleDelete(h.id)}>
                          <DeleteIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <div className="modal-backdrop" onClick={this.closeModal}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h2>{editing ? 'Edit Hotel' : 'Add Hotel'}</h2>

              {error && <div className="error-banner">{error}</div>}

              <form onSubmit={this.handleSubmit}>
                <div className="form-row">
                  <div className="field">
                    <label>Name Of Hotel</label>
                    <input name="name" value={form.name} onChange={this.handleFormChange} required />
                  </div>
                  <div className="field">
                    <label>Trade Name</label>
                    <input name="trade_name" value={form.trade_name} onChange={this.handleFormChange} />
                  </div>
                </div>

                <div className="field">
                  <label>GST Number</label>
                  <input name="gst_number" value={form.gst_number} onChange={this.handleFormChange} />
                </div>

                <div className="field">
                  <label>Address1</label>
                  <input name="address_line1" value={form.address_line1} onChange={this.handleFormChange} />
                </div>
                <div className="field">
                  <label>Address2</label>
                  <input name="address_line2" value={form.address_line2} onChange={this.handleFormChange} />
                </div>
                <div className="field">
                  <label>Address3</label>
                  <input name="address_line3" value={form.address_line3} onChange={this.handleFormChange} />
                </div>

                <div className="form-row">
                  <div className="field">
                    <label>City</label>
                    <input name="city" value={form.city} onChange={this.handleFormChange} />
                  </div>
                  <div className="field">
                    <label>PIN Code</label>
                    <input name="pincode" value={form.pincode} onChange={this.handleFormChange} />
                  </div>
                </div>

                <div className="field">
                  <label>State</label>
                  <select name="state_id" value={form.state_id} onChange={this.handleFormChange}>
                    <option value="">Select state</option>
                    {states.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={this.closeModal}>Cancel</button>
                  <button type="submit" className="btn btn-brass" disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </Layout>
    );
  }
}

export default Hotels;